import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { api } from "../lib/api.js";

export const Route = createFileRoute("/follow-ups/")({ component: FollowUpsPage });

interface FollowUp {
  id: string;
  notes: string;
  dueDate: number | null;
  completedAt: number | null;
  createdAt: number;
  guest: {
    id: string;
    name: string;
    email: string;
  };
}

function FollowUpsPage() {
  const { user } = useAuth();
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [filter, setFilter] = useState<"all" | "pending" | "completed">("pending");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get(`/api/follow-ups?status=${filter}`)
      .then(r => r.json())
      .then(d => { setFollowUps(d.followUps || []); setLoading(false); });
  }, [filter, user]);

  const completeFollowUp = async (id: string) => {
    await api.post(`/api/follow-ups/${id}/complete`);
    setFollowUps(prev => prev.map(f => f.id === id ? { ...f, completedAt: Date.now() } : f));
  };

  const deleteFollowUp = async (id: string) => {
    if (!confirm("Delete this follow-up?")) return;
    await api.delete(`/api/follow-ups/${id}`);
    setFollowUps(prev => prev.filter(f => f.id !== id));
  };

  if (!user) return <div>Loading...</div>;

  const overdue = followUps.filter(f => !f.completedAt && f.dueDate && f.dueDate < Date.now());
  const upcoming = followUps.filter(f => !f.completedAt && (!f.dueDate || f.dueDate >= Date.now()));
  const completed = followUps.filter(f => f.completedAt);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Follow-ups</h1>
        <Link to="/guests" className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium">+ New Follow-up</Link>
      </div>

      <div className="flex gap-2">
        {(["all", "pending", "completed"] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${
              filter === f ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : (
        <div className="space-y-4">
          {filter === "pending" && overdue.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900">
              <div className="px-4 py-2 border-b border-red-200 dark:border-red-900 font-medium text-red-800 dark:text-red-200">
                Overdue ({overdue.length})
              </div>
              <div className="divide-y divide-red-200 dark:divide-red-900">
                {overdue.map(renderFollowUpItem)}
              </div>
            </div>
          )}

          {filter === "pending" && upcoming.length > 0 && (
            <div className="rounded-lg border">
              <div className="px-4 py-2 border-b bg-muted font-medium">
                Upcoming ({upcoming.length})
              </div>
              <div className="divide-y">
                {upcoming.map(renderFollowUpItem)}
              </div>
            </div>
          )}

          {filter === "completed" && completed.length > 0 && (
            <div className="rounded-lg border">
              <div className="px-4 py-2 border-b bg-muted font-medium">
                Completed ({completed.length})
              </div>
              <div className="divide-y">
                {completed.map(renderFollowUpItem)}
              </div>
            </div>
          )}

          {filter === "all" && followUps.length > 0 && (
            <div className="rounded-lg border">
              <div className="divide-y">
                {followUps.map(renderFollowUpItem)}
              </div>
            </div>
          )}

          {followUps.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No follow-ups. <Link to="/guests" className="text-primary hover:underline">Create one from a guest</Link>.
            </div>
          )}
        </div>
      )}
    </div>
  );

  function renderFollowUpItem(followUp: FollowUp) {
    const isOverdue = !followUp.completedAt && followUp.dueDate && followUp.dueDate < Date.now();
    return (
      <div key={followUp.id} className={`p-4 flex items-start gap-4 ${isOverdue ? "bg-red-50/50 dark:bg-red-950/20" : ""}`}>
        {!followUp.completedAt && (
          <button
            onClick={() => completeFollowUp(followUp.id)}
            className="mt-0.5 w-5 h-5 rounded border-2 border-muted-foreground/30 hover:border-primary hover:bg-primary/10 flex items-center justify-center"
            title="Mark complete"
          >
            ✓
          </button>
        )}
        {followUp.completedAt && (
          <div className="mt-0.5 w-5 h-5 rounded bg-green-500 text-white flex items-center justify-center text-xs">✓</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/guests/$guestId" params={{ guestId: followUp.guest.id }} className="font-medium hover:text-primary">
              {followUp.guest.name}
            </Link>
            <span className="text-sm text-muted-foreground">{followUp.guest.email}</span>
          </div>
          <p className={`mt-1 ${followUp.completedAt ? "line-through text-muted-foreground" : ""}`}>{followUp.notes}</p>
          {followUp.dueDate && (
            <p className={`text-sm mt-1 ${isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
              Due: {new Date(followUp.dueDate).toLocaleDateString()}
              {isOverdue && " (overdue)"}
            </p>
          )}
        </div>
        <button
          onClick={() => deleteFollowUp(followUp.id)}
          className="text-muted-foreground hover:text-red-500 text-sm"
          title="Delete"
        >
          ×
        </button>
      </div>
    );
  }
}
